/**
 * GTM — deferred global loader.
 *
 * Loads Google Tag Manager AFTER hydration so it stays off the critical render
 * path, while GUARANTEEING it always loads on every pageview.
 *
 * Why a global plugin (not the `useGtm()` composable): a previous attempt moved
 * GTM loading into `useGtm()`, which is only called from the contact form
 * (`useContactFormActions.ts`). As a result GTM — and the GA / Facebook tags
 * inside the container — only loaded when a user opened that form, so normal
 * pageviews never loaded GTM at all. Loading here, globally, restores
 * load-on-every-page while keeping the defer.
 *
 * Why a guaranteed timeout (not `requestIdleCallback` alone): `requestIdleCallback`
 * can be starved indefinitely in background / inactive tabs, which made GTM fail
 * to load for those sessions. The trigger resolves on the FIRST of an idle
 * callback OR a fixed 2s timeout, so it always fires shortly after hydration.
 *
 * A Promise trigger (rather than the string `'onNuxtReady'`) is used so we can
 * keep `warmupStrategy: false` — the string trigger forces a `preload` warmup,
 * which re-introduces the "preloaded but unused" warning and partially defeats
 * the defer.
 */
export default defineNuxtPlugin(() => {
	// Never load GTM in unit/component tests.
	if (import.meta.env.VITEST || import.meta.env.MODE === 'test') return;

	const gtmId = useRuntimeConfig().public.gtmId as string;
	if (!gtmId || gtmId.trim() === '') return;

	const trigger = new Promise<void>(resolve => {
		onNuxtReady(() => {
			let settled = false;
			let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

			const fire = () => {
				if (settled) return;
				settled = true;
				if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
				resolve();
			};

			// Guaranteed fallback: a plain timer still fires in background tabs
			// (where requestIdleCallback can be starved), so GTM never fails to
			// load. requestIdleCallback just lets it fire earlier when idle.
			fallbackTimer = setTimeout(fire, 2000);
			if ('requestIdleCallback' in window) {
				requestIdleCallback(fire, { timeout: 2000 });
			}
		});
	});

	useScriptGoogleTagManager({
		id: gtmId,
		scriptOptions: {
			trigger,
			warmupStrategy: false
		}
	});
});
